import { User } from '../auth/User';
import { ICompanySummary } from '../shell/ICompanySummary';
import { PluginAccessItem } from '../shell/IShellPluginConfig';
import { Company } from '../types/Company';
import { ApiResponse } from './ApiResponse';
import {
  ApiParams,
  ApiRoutes,
  constructApiParams,
  constructNexusApiParams,
  getFullApiRoute,
  replaceRouteParams,
} from './ApiRoutes';
import { HttpRequest } from './HttpRequest';
import { IApiPaginationMetadata } from './IApiPaginationMetadata';

export interface CompanyFilterOptions {
  accountManagers: string[];
  targetMarkets: string[];
}

export class CompanyAPI {
  //This will get all companines - is a super admin only api
  static async getAll(
    user: User | null,
    params: ApiParams,
  ): Promise<(ApiResponse & { data: Partial<Company>[] }) | undefined> {
    const urlParams = new URLSearchParams({
      page: String(params.page || 1),
      limit: String(params.per_page || 10),
    });

    if (params.sortField) {
      urlParams.append('sortField', String(params.sortField));
    }

    if (params.sortDir) {
      urlParams.append('sortDir', String(params.sortDir));
    }

    const url = `${getFullApiRoute(ApiRoutes.COMPANIES_V3)}?${urlParams.toString()}`;
    const result = await HttpRequest(constructNexusApiParams(url), 'GET', null, null, user);
    return result && result.data
      ? (result as ApiResponse & { data: Partial<Company>[] })
      : undefined;
  }

  static async getAllFilteredByName(
    user: User | null,
    params: ApiParams,
  ): Promise<(ApiResponse & { data: Partial<Company>[] }) | undefined> {
    const result = await HttpRequest(
      constructApiParams(getFullApiRoute(ApiRoutes.SEARCH_COMPANIES), params),
      'GET',
      null,
      null,
      user,
    );
    return result && result.data
      ? (result as ApiResponse & { data: Partial<Company>[] })
      : undefined;
  }

  static async filter(
    user: User | null,
    filters: Record<string, string | undefined>,
    params: ApiParams,
    fetchCompaniesForInvite = false,
  ): Promise<(ApiResponse & { data: Partial<Company>[] }) | undefined> {
    const urlParams = new URLSearchParams();

    // Add pagination parameters from ApiParams
    urlParams.append('page', String(params.page));
    urlParams.append('limit', String(params.per_page));

    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        urlParams.append(key, value);
      }
    });

    const url = `${getFullApiRoute(fetchCompaniesForInvite ? ApiRoutes.COMPANIES_LISTING_FOR_INVITE_V3 : ApiRoutes.COMPANIES_FILTER_V3)}?${urlParams.toString()}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result && result.data
      ? (result as ApiResponse & { data: Partial<Company>[] })
      : undefined;
  }

  static async getByName(
    user: User | null,
    name: string,
    params: ApiParams,
  ): Promise<(ApiResponse & { data: Partial<Company>[] }) | undefined> {
    const urlParams = new URLSearchParams({
      name,
      page: String(params.page),
      limit: String(params.per_page),
    });

    const url = `${getFullApiRoute(ApiRoutes.COMPANIES_BY_NAME_V3)}?${urlParams.toString()}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result && result.data
      ? (result as ApiResponse & { data: Partial<Company>[] })
      : undefined;
  }

  static async getFilterOptions(user: User | null): Promise<CompanyFilterOptions | null> {
    const url = getFullApiRoute(ApiRoutes.COMPANIES_FILTER_OPTIONS_V3);
    const result = await HttpRequest(url, 'GET', null, null, user);
    // API returns { data: [{ accountManagers: [...], targetMarkets: [...] }] }
    return result && result.data && result.data[0] ? result.data[0] : null;
  }

  static async getOnboardingStatus(
    user: User,
    companyId: string,
  ): Promise<{ onboardingStatus: string } | null> {
    const url = replaceRouteParams(
      getFullApiRoute(ApiRoutes.COMPANIES_ONBOARDING_STATUS_V3),
      { companyId },
    );
    const result = await HttpRequest(url, 'GET', null, null, user);
    if (!result?.data) {
      return null;
    }
    const row = Array.isArray(result.data) ? result.data[0] : result.data;
    return row ?? null;
  }

  static async updateOnboardingStatus(
    user: User,
    companyId: string,
    onboardingStatus: string,
  ): Promise<{ onboardingStatus: string } | null> {
    const url = replaceRouteParams(
      getFullApiRoute(ApiRoutes.COMPANIES_ONBOARDING_STATUS_V3),
      { companyId },
    );
    const result = await HttpRequest(
      url,
      'PATCH',
      { onboardingStatus },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    if (!result?.data) {
      return null;
    }
    const row = Array.isArray(result.data) ? result.data[0] : result.data;
    return row ?? null;
  }

  //Get a single company
  static async getOne(user: User, companyId: string): Promise<Company | null> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.COMPANIES_V3) + `/${companyId}`,
      'GET',
      null,
      null,
      user,
    );
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : null;
  }

  //Export company data as CSV
  static async exportCSV(user: User, companyId: string): Promise<{ data: string } | undefined> {
    const result = await HttpRequest(
      `${process.env['NX_PUBLIC_ONBOARDING_API_ENDPOINT']}/onboarding/v1/businesses/${companyId}?export=csv`,
      'GET',
      null,
      null,
      user,
    );
    return result && result.data ? result : undefined;
  }

  // Update a company - PUT request to /v2/companies/:id
  static async updateOne(
    user: User,
    companyId: string,
    data: Partial<Company>,
  ): Promise<Company | undefined> {
    const url = getFullApiRoute(ApiRoutes.COMPANIES_V3) + `/${companyId}`;
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const result = await HttpRequest(url, 'PUT', data, headers, user);

    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  // Update plugin permissions for a company - PUT request to /v2/companies/:id/plugin-permissions
  static async updatePluginPermissions(
    user: User,
    companyId: string,
    pluginIds: string[],
  ): Promise<{ company: Company; pluginAccess: Array<PluginAccessItem> } | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.COMPANIES_V3) + `/${companyId}/plugin-permissions`,
      'PUT',
      { pluginIds },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    //Check if we have a result and if it is object or array, if array just return the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async getCompanyListing(
    user: User | null,
    params: ApiParams,
  ): Promise<
    | (ApiResponse & { data: Partial<ICompanySummary>[]; metadata: IApiPaginationMetadata })
    | undefined
  > {
    const result = await HttpRequest(
      constructApiParams(getFullApiRoute(ApiRoutes.COMPANIES_LISTING_V3), params, true),
      'GET',
      null,
      null,
      user,
    );

    return result && result.data
      ? (result as ApiResponse & {
          data: Partial<ICompanySummary>[];
          metadata: IApiPaginationMetadata;
        })
      : undefined;
  }

  static async searchTargetMarkets(
    user: User | null,
    search: string,
    params: ApiParams,
  ): Promise<(ApiResponse & { data: string[]; metadata: IApiPaginationMetadata }) | undefined> {
    const urlParams = new URLSearchParams({
      search,
      page: String(params.page),
      limit: String(params.per_page),
    });

    const result = await HttpRequest(
      `${getFullApiRoute(ApiRoutes.COMPANIES_TARGET_MARKETS_V3)}?${urlParams.toString()}`,
      'GET',
      null,
      null,
      user,
    );
    return result && result.data
      ? (result as ApiResponse & { data: string[]; metadata: IApiPaginationMetadata })
      : undefined;
  }

  static async getSingleCompanyListing(
    user: User | null,
    companyId: string,
  ): Promise<
    (ApiResponse & { data: Partial<ICompanySummary>; metadata: IApiPaginationMetadata }) | undefined
  > {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.COMPANIES_LISTING_V3) + `?search=${companyId}&page=1&limit=1`,
      'GET',
      null,
      null,
      user,
    );

    return result && result.data
      ? (result as ApiResponse & {
          data: Partial<ICompanySummary>;
          metadata: IApiPaginationMetadata;
        })
      : undefined;
  }

  /**
   *
   * @param user - Current Authenticated user
   * @param filters - For fetching specific record
   * @returns - List of companies associated with the current user
   */
  static async getCurrentUserCompanies(
    user: User | null,
    filters: { keyword?: string; page: number; limit: number },
  ): Promise<(ApiResponse & { data: Partial<Company>[] }) | undefined> {
    const searchParams = new URLSearchParams();

    // Append query parameters based on the provided filters
    if (filters.keyword) searchParams.append('keyword', filters.keyword);
    searchParams.append('page', String(filters.page));
    searchParams.append('limit', String(filters.limit));

    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.CURRENT_USER_COMPANIES_V3) + `?${searchParams.toString()}`,
      'GET',
      null,
      new Headers(),
      user,
    );
    return result && result.data
      ? (result as ApiResponse & { data: Partial<Company>[] })
      : undefined;
  }

  /**
   *
   * @param user - Current Authenticated user
   * @param filters - For fetching specific record
   * @param userId - to fetch data of specific User
   * @returns - List of companies associated with the user
   */
  static async getUserCompanies(
    user: User | null,
    filters: { keyword?: string; page: number; limit: number },
    userId: string,
  ): Promise<(ApiResponse & { data: Partial<Company>[] }) | undefined> {
    const searchParams = new URLSearchParams();

    // Append query parameters based on the provided filters
    if (filters.keyword) searchParams.append('keyword', filters.keyword);
    searchParams.append('page', String(filters.page));
    searchParams.append('limit', String(filters.limit));

    const result = await HttpRequest(
      getFullApiRoute(replaceRouteParams(ApiRoutes.USER_COMPANIES_V3, { userId: userId })) +
        `?${searchParams.toString()}`,
      'GET',
      null,
      new Headers(),
      user,
    );
    return result && result?.data
      ? (result as ApiResponse & { data: Partial<Company>[] })
      : undefined;
  }

  /**
   *
   * @param user - Current Authenticated user
   * @param companyId - Data containing the companyId to leave
   * @returns - Success status of the leave company operation
   */
  static async leaveCompany(
    user: User | null,
    companyId: string,
  ): Promise<{ success: boolean } | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(
        replaceRouteParams(ApiRoutes.CURRENT_USER_LEAVE_COMPANY_V3, { id: companyId }),
      ),
      'DELETE',
      null,
      new Headers(),
      user,
    );
    return result && result.data ? result : undefined;
  }

  /**
   * Deletes a company with the specified ID.
   */
  static async deleteCompany(
    user: User | null,
    companyId: string,
  ): Promise<{ success: boolean } | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(replaceRouteParams(ApiRoutes.DELETE_COMPANY_V3, { companyId: companyId })),
      'DELETE',
      null,
      new Headers(),
      user,
    );
    return result && result.data ? result : undefined;
  }

  /**
   * Creates a new company with the provided data.
   */
  static async createCompany(user: User | null, companyData: Partial<Company>): Promise<Company> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.COMPANIES_V3),
      'POST',
      companyData,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result;
  }
}
